<template>
  <div class="manual-text">
    <div>
      <h1 class="text-center mt-2 mb-10">View Project</h1>
      <p>
        To perform actions for a project, like viewing or editing its KPIs/PSIs, running analyses, setting weights, etc.,
        the user must first <b>navigate</b> to the project's <b>"Details Page".</b> To do so, the user should locate the
        project of interest and then click the <b>blue button with the <i class="fas fa-eye" /> eye icon.</b>
      </p>

      <div class="image-container">
        <img src="@/images/manual/projects-page-view-button.png" alt="view project button" />
        <div class="img-caption">Figure 1. The "View Project" button's location.</div>
      </div>

      <p>
        After the button is clicked, the user will be redirected to the corresponding project's Details Page, which
        contains several key actions to perform: i) perform SII & SPI or PPI analysis by clicking the corresponding
        <b>analysis button</b>, located to the <b>top-right corner</b> of the KPIs/PSIs tables (for more details see
        the PPI Analysis, SII & SPI Analysis sections), ii) set ignored weights by clicking the corresponding button,
        iii) manage KPIs and PSIs.
      </p>

      <div class="image-container">
        <img src="@/images/manual/project-analyses-buttons.png" alt="project analyses buttons" />
        <div class="img-caption">Figure 2. The analyses buttons' location</div>
      </div>

      <p>
        To switch between the KPIs and PSIs list the user can use the <b>tab bar</b> on the top of the KPIs / PSIs tables.
        Clicking each tab leads to the corresponding data table.
      </p>

      <div class="image-container">
        <img src="@/images/manual/projects-tab-bar.png" alt="kpi psi tab bar" />
        <div class="img-caption">Figure 3. The tab bar that switches from KPIs to PSIs table.</div>
      </div>

      <p>
        Below the tab bar, there is a <b>text search field</b>, which can be used to search a KPI or a PSI (depending on
        the active tab) by any attribute of the indicator (e.g. id, name, dimension, call impact score etc).
      </p>

      <div class="image-container">
        <img src="@/images/manual/projects-search-example.png" alt="project search example" />
        <div class="img-caption">Figure 4. The search bar with an example key-word searched.</div>
      </div>

      <p>
        For every KPI/PSI there are <b>three actions</b> the user can take. The first option is the <b>Detailed View</b>,
        by clicking the <i class="fas fa-eye"/> <b>eye icon</b>, which redirects the user to the indicator's page that
        presents the indicator in detail. Second, there is the <b>Edit</b> button, by clicking the
        <i class="fas fa-pen" /> <b>pencil icon</b>, which redirects the user to the edit form of the corresponding KPI/PSI.
        Lastly, the <b>Delete</b> button, by clicking the <i class="fas fa-trash" /> <b>trash icon</b>, which opens a
        confirmation modal that, once confirmed, deletes the corresponding KPI/PSI from the project.
      </p>

      <div class="image-container">
        <v-row>
          <v-col>
            <img src="@/images/manual/project-kpi-psi-actions.png" alt="kpi psi actions" />
          </v-col>
          <v-col>
            <img src="@/images/manual/delete-kpi-psi-modal.png" alt="delete kpi psi modal" />
          </v-col>
        </v-row>
        <div class="img-caption">Figure 5. The action buttons' location (left) and the confirm delete prompt of a KPI/PSI (right).</div>
      </div>

      <p>
        Finally, the <b>"Set Ignored Weights" button</b>, located on top of the <b>tab bar</b>, opens a modal in which
        the user can choose the weights to be ignored in the project. In this modal, the user is prompted to select multiple
        <b>Aggregation Level</b> and <b>Dimension/Sector</b> pairs; all of which their weights will be ignored in the
        analysis. Once the user is satisfied with the pairs selected, clicking <b>"Save Preferences"</b> will save these
        options.
      </p>

      <div class="image-container">
        <img src="@/images/manual/set-ignored-weights-modal.png" alt="ignored weights modal" />
        <div class="img-caption">Figure 6. The prompt shown to the user, when the "Set Ignore Weights" button is clicked.</div>
      </div>

      <p>
        <i class="text-grey-darken-4">(The KPIs/PSIs overview button is explained in a different section.)</i>
      </p>

    </div>
  </div>
</template>

<script setup>

</script>

<style scoped>

</style>
